/**
 * PricingPage.jsx — Free + 3-tier version.
 * Tiers: free, ai_tools (AI Tool), ai_tools_plus (AI + Chrome Extension),
 * ai_premium (AI Premium). Cards are driven entirely by whatever active
 * plans /api/subscriptions/plans/ returns — feature rows (AI requests/day,
 * Chrome extension access, ads removed) are read straight off each plan
 * object, so pricing/limit changes on the backend need no frontend edit.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const T = {
  bg:'#07080c', bg2:'#0c0f18', surf:'#161c2a',
  border:'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.13)',
  white:'#dce6fa', muted:'rgba(255,255,255,0.50)', dim:'rgba(255,255,255,0.50)',
  cyan:'#00c8f0', blue:'#4d8fff', green:'#2ecc71',
  red:'#e74c3c', yellow:'#f1c40f', purple:'#b78cff',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
};

// Visual metadata per tier — only rendered if the backend returns that plan
const TIER_META = {
  free:          { icon:'🌱', color:T.muted,   popular:false },
  ai_tools:      { icon:'🤖', color:T.cyan,    popular:false },
  ai_tools_plus: { icon:'🚀', color:T.yellow,  popular:true  },
  ai_premium:    { icon:'👑', color:T.purple,  popular:false },
};

// Feature rows are computed from the plan object itself (ai_requests_per_day,
// allows_chrome_extension, removes_ads) so limits/pricing changes on the
// backend show up here automatically — no hardcoded per-tier list to update.
function getFeatures(plan) {
  if (plan.tier === 'free') {
    return [
      { t:'All 74+ free tools',          ok:true  },
      { t:'AI tools (Job Search, etc.)', ok:false },
      { t:'Chrome extensions',           ok:false },
    ];
  }
  return [
    { t:'All 74+ free tools', ok:true },
    { t:'All AI portals (Job Search, Freelancer, Developer)', ok:true },
    { t:`${plan.ai_requests_per_day} AI requests/day`, ok:true },
    { t: plan.allows_chrome_extension ? 'Chrome extensions (all 3)' : 'Chrome extensions', ok: !!plan.allows_chrome_extension },
    { t:'Ads removed', ok: !!plan.removes_ads },
  ];
}

function FeatureRow({ f }) {
  return (
    <li style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', fontSize:13, color:f.ok ? T.white : T.dim }}>
      <span style={{ color:f.ok ? T.green : T.dim+'60', fontSize:14 }}>{f.ok ? '✓' : '✗'}</span>
      {f.t}
    </li>
  );
}

export default function PricingPage() {
  const [interval, setIntervalMode] = useState('monthly');
  const [plans,    setPlans]        = useState([]);
  const [loading,  setLoading]      = useState(null);
  const [error,    setError]        = useState('');
  const { isAuthenticated, tier, apiFetch, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const success = new URLSearchParams(location.search).get('success');

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/subscriptions/plans/`)
      .then(r => r.json())
      .then(setPlans)
      .catch(() => {});
  }, []);

  useEffect(() => { if (success) refreshSubscription(); }, [success]);

  // Only tiers the backend actually returned (is_active=true) get a card
  const tierOrder = ['free', 'ai_tools', 'ai_tools_plus', 'ai_premium'];
  const displayPlans = tierOrder
    .map(t => plans.find(p => p.tier === t && p.interval === interval)
            || plans.find(p => p.tier === t && p.interval === 'monthly'))
    .filter(Boolean);

  const handleSelect = async (plan) => {
    if (plan.tier === 'free' || tier === plan.tier) return;
    if (!isAuthenticated) { navigate('/login', { state:{ from:'/pricing' } }); return; }

    setLoading(plan.id); setError('');
    try {
      const res  = await apiFetch('/api/subscriptions/checkout/', {
        method: 'POST',
        body:   JSON.stringify({ plan_id: plan.id, interval }),
      });
      console.log(JSON.stringify({ plan_id: plan.id, interval }))
      const data = await res.json();
      console.log(data)
      if (!res.ok) throw new Error(data.error || 'Checkout failed');
      if(data.checkout_url){
          window.location.href = data.checkout_url;

      }
      else{
              window.location.href = '/dashboard';

      }
    } catch(e) {
      setError(e.message);
      setLoading(null);
    }
  };

  return (
    <>
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>

      <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.sans, padding:'0 0 64px' }}>

        {success && (
          <div style={{ padding:'14px 24px', textAlign:'center', background:T.green+'15', borderBottom:`1px solid ${T.green}30`, fontSize:13.5, color:T.green, fontWeight:600 }}>
            🎉 Payment successful! Your plan is now active.
          </div>
        )}

        <div style={{ textAlign:'center', padding:'56px 24px 40px' }}>
          <h1 style={{ fontSize:'clamp(2rem,5vw,3.2rem)', fontWeight:900, color:T.white, letterSpacing:'-.03em', marginBottom:12 }}>
            Choose your plan
          </h1>
          <p style={{ fontSize:15, color:T.muted, maxWidth:480, margin:'0 auto 28px' }}>
            Mix and match exactly what you need. Cancel anytime.
          </p>

          <div style={{ display:'inline-flex', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:9, padding:4 }}>
            {['monthly','yearly'].map(iv => (
              <button key={iv} onClick={() => setIntervalMode(iv)} style={{
                padding:'7px 20px', borderRadius:6, border:'none',
                background: interval===iv ? T.surf : 'transparent',
                color: interval===iv ? T.white : T.muted,
                fontSize:13, fontWeight: interval===iv ? 600 : 400,
                fontFamily:T.sans, cursor:'pointer',
              }}>
                {iv.charAt(0).toUpperCase()+iv.slice(1)}
                {iv==='yearly' && <span style={{ marginLeft:7, padding:'1px 7px', borderRadius:20, background:T.green+'20', fontSize:10.5, color:T.green, fontWeight:700 }}>-25%</span>}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div style={{ maxWidth:500, margin:'0 auto 20px', padding:'10px 16px', borderRadius:8, background:T.red+'12', border:`1px solid ${T.red}40`, color:T.red, fontSize:13, textAlign:'center' }}>
            {error}
          </div>
        )}

        <div style={{
          maxWidth:1200, margin:'0 auto', padding:'0 24px',
          display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))', gap:14,
        }}>
          {displayPlans.map(plan => {
            const meta     = TIER_META[plan.tier]  || {};
            const features = getFeatures(plan);
            const price    = plan.price_cents / 100;
            const isCurrent= tier === plan.tier;
            const isFree   = plan.tier === 'free';
            const isLoading= loading === plan.id;

            return (
              <div key={plan.id} style={{
                position:'relative',
                background: meta.popular ? `linear-gradient(135deg,${meta.color}14,${meta.color}07)` : 'rgba(255,255,255,.025)',
                border:`1px solid ${isCurrent ? meta.color+'60' : meta.popular ? meta.color+'40' : T.border}`,
                borderRadius:16, padding:'24px 20px',
              }}>
                {meta.popular && (
                  <div style={{ position:'absolute', top:-12, left:'50%', transform:'translateX(-50%)', padding:'3px 14px', borderRadius:20, background:`linear-gradient(90deg,${T.cyan},${T.blue})`, color:'#07080c', fontSize:10.5, fontWeight:700, whiteSpace:'nowrap' }}>
                    Popular
                  </div>
                )}
                {isCurrent && (
                  <div style={{ position:'absolute', top:12, right:12, padding:'2px 9px', borderRadius:20, background:T.green+'20', border:`1px solid ${T.green}40`, color:T.green, fontSize:10, fontWeight:700 }}>
                    Current
                  </div>
                )}

                <div style={{ fontSize:24, marginBottom:8 }}>{meta.icon}</div>
                <div style={{ fontSize:15, fontWeight:800, color:T.white, marginBottom:4, lineHeight:1.25, minHeight:38 }}>{plan.name.replace(/ — (Monthly|Yearly)$/, '')}</div>

                <div style={{ marginBottom:16 }}>
                  <span style={{ fontSize:28, fontWeight:900, color:meta.color }}>
                    {price === 0 ? 'Free' : `$${price.toFixed(2)}`}
                  </span>
                  {price > 0 && <span style={{ fontSize:12, color:T.muted, marginLeft:3 }}>/{interval==='monthly'?'mo':'yr'}</span>}
                </div>

                <ul style={{ listStyle:'none', marginBottom:18 }}>
                  {features.map((f,i) => <FeatureRow key={i} f={f}/>)}
                </ul>

                <button
                  onClick={() => handleSelect(plan)}
                  disabled={isCurrent || isFree || !!loading}
                  style={{
                    width:'100%', padding:'10px', borderRadius:8,
                    background: isCurrent ? T.green+'18' : isFree ? 'transparent' : meta.popular ? `linear-gradient(135deg,${T.cyan},${T.blue})` : `${meta.color}18`,
                    border:`1px solid ${isCurrent ? T.green+'40' : isFree ? T.border : meta.color+'50'}`,
                    color: isCurrent ? T.green : isFree ? T.muted : meta.popular ? '#07080c' : meta.color,
                    fontSize:12.5, fontWeight:700, fontFamily:T.sans,
                    cursor: (isCurrent || isFree || loading) ? 'default' : 'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                  }}>
                  {isLoading ? 'Redirecting…' : isCurrent ? '✓ Current' : isFree ? 'Free forever' : 'Select →'}
                </button>
              </div>
            );
          })}
        </div>

        <div style={{ maxWidth:700, margin:'36px auto 0', padding:'0 24px', textAlign:'center', fontSize:12, color:T.dim }}>
          Payments by Stripe · Cancel anytime
        </div>
      </div>
    </>
  );
}
